/**
 * LeadLogin.js  —  My Policy Access login page
 * Primary: magic login link (email / SMS)
 * Secondary: email + password
 * Social: Google / Facebook / Apple
 * Handles: ?magic=TOKEN auto-login from link, ?token=JWT from OAuth redirect
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
// Capacitor plugins — safely imported so the web bundle still works
// (they are no-ops when running in a browser)
import { Capacitor } from '@capacitor/core';
import { Browser }   from '@capacitor/browser';
import { App as CapApp } from '@capacitor/app';
import { FacebookLogin } from '@capacitor-community/facebook-login';
import { SignInWithApple } from '@capacitor-community/apple-sign-in';

const API       = process.env.REACT_APP_API_URL || '';
const OAUTH_BASE = process.env.REACT_APP_OAUTH_URL || process.env.REACT_APP_API_URL || '';
const isNative  = Capacitor.isNativePlatform(); // true = iOS / Android app
const isIos     = Capacitor.getPlatform() === 'ios'; // true only on iPhone/iPad

/**
 * Open an OAuth URL.
 * • Native app → opens in system browser, sends ?return=app so the backend
 *   redirects back to the mypolicyaccess:// custom URL scheme.
 * • Web / PWA   → normal top-level redirect.
 */
async function openOAuth(provider) {
  const base = `${OAUTH_BASE}/api/lead-auth/oauth/${provider}`;
  if (isNative) {
    await Browser.open({ url: `${base}?return=app`, presentationStyle: 'fullscreen' });
  } else {
    window.location.href = base;
  }
}

const C = {
  brand: '#1a56db',
  bg: '#f9fafb',
  card: '#ffffff',
  border: '#e5e7eb',
  text: '#111827',
  muted: '#6b7280',
  danger: '#e02424',
  success: '#0e9f6e',
};

/* ─── Social button ────────────────────────────────────── */
const SOCIAL_ICONS = {
  google: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908C16.658 14.258 17.64 11.92 17.64 9.2z" fill="#4285F4"/>
      <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z" fill="#34A853"/>
      <path d="M3.964 10.707A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.707V4.961H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.039l3.007-2.332z" fill="#FBBC05"/>
      <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.961L3.964 6.293C4.672 4.166 6.656 3.58 9 3.58z" fill="#EA4335"/>
    </svg>
  ),
  facebook: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="#fff">
      <path d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.41c0-3.025 1.792-4.697 4.533-4.697 1.312 0 2.686.236 2.686.236v2.97h-1.513c-1.491 0-1.956.93-1.956 1.883v2.252h3.328l-.532 3.49H13.875V24C19.612 23.094 24 18.1 24 12.073z"/>
    </svg>
  ),
  apple: (
    <svg width="17" height="20" viewBox="0 0 24 24" fill="#fff" xmlns="http://www.w3.org/2000/svg">
      <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.7 9.05 7.44c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.39-1.32 2.76-2.53 3.95zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
    </svg>
  ),
};
const SOCIAL_S = {
  google:   { bg: '#fff',    color: '#3c4043', border: '#dadce0', shadow: '0 1px 3px rgba(0,0,0,.12)' },
  facebook: { bg: '#1877f2', color: '#fff',    border: '#1877f2', shadow: 'none' },
  apple:    { bg: '#000',    color: '#fff',    border: '#000',    shadow: 'none' },
};

function SocialBtn({ provider, icon, label, onClick, bg = '#fff', color = '#111' }) {
  const s = provider ? SOCIAL_S[provider] : { bg, color, border: bg === '#fff' ? C.border : bg, shadow: 'none' };
  const iconEl = provider
    ? SOCIAL_ICONS[provider]
    : <span style={{ fontSize: 18, lineHeight: 1 }}>{icon}</span>;
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
        width: '100%', padding: '11px 14px',
        background: s.bg, color: s.color,
        border: `1.5px solid ${s.border}`,
        borderRadius: 8, fontSize: 14, fontWeight: 600,
        cursor: 'pointer', marginBottom: 10,
        boxShadow: s.shadow,
      }}
    >
      {iconEl}
      {label}
    </button>
  );
}

function Divider({ text }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', margin: '16px 0' }}>
      <div style={{ flex: 1, height: 1, background: C.border }} />
      <span style={{ padding: '0 12px', fontSize: 12, color: C.muted }}>{text}</span>
      <div style={{ flex: 1, height: 1, background: C.border }} />
    </div>
  );
}

export default function LeadLogin({ onLogin, onSignup }) {
  const [mode, setMode]         = useState('magic'); // 'magic' | 'password'
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading]   = useState(false);
  const [magicSent, setMagicSent] = useState(false);
  const [error, setError]       = useState('');

  // Facebook email modal state — shown when FB login succeeds but no email in JWT
  const [fbEmailModal, setFbEmailModal]       = useState(false);
  const [fbEmailInput, setFbEmailInput]       = useState('');
  const [fbEmailError, setFbEmailError]       = useState('');
  const [fbEmailLoading, setFbEmailLoading]   = useState(false);
  const [fbPendingToken, setFbPendingToken]   = useState(null); // iOS: authenticationToken for retry
  const [fbPendingNonce, setFbPendingNonce]   = useState(null);
  const [fbPendingCtx, setFbPendingCtx]       = useState(null); // Web: base64url fbCtx from URL
  const [fbName, setFbName]                   = useState('');
  // emailTaken state — shown inside the same modal when user's email already exists
  const [fbEmailTaken, setFbEmailTaken]       = useState(false);
  const [fbTakenEmail, setFbTakenEmail]       = useState('');
  const [fbMagicSent, setFbMagicSent]         = useState(false); // one-time magic link sent flag
  // Apple — reuses the same modal, just tracks the pending identityToken for retry
  const [applePendingToken, setApplePendingToken] = useState(null);
  const [modalProvider, setModalProvider]         = useState('facebook'); // 'facebook' | 'apple'

  const handleFacebook = async () => {
    if (!isNative) { openOAuth('facebook'); return; }
    setLoading(true); setError('');
    try {
      // Limited login — works reliably on iOS 17 without ATT permission.
      // Returns an AuthenticationToken (OIDC JWT) containing email + name.
      const nonce = Math.random().toString(36).slice(2);
      const result = await FacebookLogin.login({
        permissions: ['email', 'public_profile'],
        tracking: 'limited',
        nonce,
      });
      // Plugin always returns under accessToken.token (even for Limited Login)
      // because the native Swift code resolves via getCurrentAccessToken()
      const token = result?.accessToken?.token ?? null;
      if (!token) { setLoading(false); return; } // user cancelled
      const r = await axios.post(API + '/api/lead-auth/facebook-limited', {
        authenticationToken: token,
        nonce,
      });
      if (r.data?.token) {
        localStorage.setItem('leadToken', r.data.token);
        if (onLogin) onLogin(r.data.token, null, false);
        else window.location.href = r.data.onboarding ? '/lead/onboarding' : '/lead/dashboard';
      } else if (r.data?.needsEmail) {
        // Facebook authenticated but no email in JWT — show email modal
        setFbPendingToken(token);
        setFbPendingNonce(nonce);
        setFbName(r.data.fbName || '');
        setFbEmailModal(true);
      } else if (r.data?.error) {
        setError(r.data.error);
      }
    } catch (err) {
      console.warn('[FB] limited login failed:', err.message);
      const msg = err?.response?.data?.error;
      setError(msg || 'Facebook sign-in failed. Please try again.');
    } finally { setLoading(false); }
  };

  // Called when user submits email in the modal (iOS native + web, Facebook + Apple)
  const handleFbEmailSubmit = async () => {
    const emailVal = fbEmailInput.trim().toLowerCase();
    if (!emailVal || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailVal)) {
      setFbEmailError('Please enter a valid email address.');
      return;
    }
    setFbEmailLoading(true);
    setFbEmailError('');
    try {
      // Route to correct endpoint based on provider
      let endpoint, payload;
      if (modalProvider === 'apple') {
        endpoint = API + '/api/lead-auth/apple-native';
        payload  = { identityToken: applePendingToken, email: emailVal };
      } else if (fbPendingCtx) {
        endpoint = API + '/api/lead-auth/facebook-web-email';
        payload  = { fbCtx: fbPendingCtx, email: emailVal };
      } else {
        endpoint = API + '/api/lead-auth/facebook-limited';
        payload  = { authenticationToken: fbPendingToken, nonce: fbPendingNonce, email: emailVal };
      }

      const r = await axios.post(endpoint, payload);
      if (r.data?.token) {
        setFbEmailModal(false);
        localStorage.setItem('leadToken', r.data.token);
        if (onLogin) onLogin(r.data.token, null, false);
        else window.location.href = r.data.onboarding ? '/lead/onboarding' : '/lead/dashboard';
      } else if (r.data?.emailTaken) {
        // Email already exists — switch modal to "send magic link" state
        setFbEmailTaken(true);
        setFbTakenEmail(r.data.email);
      } else if (r.data?.error) {
        setFbEmailError(r.data.error);
      }
    } catch (err) {
      const msg = err?.response?.data?.error;
      setFbEmailError(msg || 'Something went wrong. Please try again.');
    } finally { setFbEmailLoading(false); }
  };

  // Send one-time magic link to the taken email
  const handleFbMagicLink = async () => {
    if (fbMagicSent) return; // prevent resend
    setFbEmailLoading(true);
    try {
      await axios.post(API + '/api/lead-auth/request-magic-link', { email: fbTakenEmail });
      setFbMagicSent(true);
    } catch (err) {
      const msg = err?.response?.data?.error;
      setFbEmailError(msg || 'Failed to send login link. Please try again.');
    } finally { setFbEmailLoading(false); }
  };
  const handleApple = async () => {
    if (!isNative) { openOAuth('apple'); return; }
    setLoading(true); setError('');
    try {
      const result = await SignInWithApple.authorize({
        clientId: 'com.mypolicyaccess.app',
        redirectURI: 'https://medicareagents.agentemp.com/api/lead-auth/oauth/apple',
        scopes: 'email name',
        state: 'native',
        nonce: Math.random().toString(36).slice(2),
      });
      const { identityToken, email, givenName, familyName } = result.response || {};
      if (!identityToken) { setLoading(false); return; }
      const name = [givenName, familyName].filter(Boolean).join(' ');
      const r = await axios.post(API + '/api/lead-auth/apple-native', { identityToken, email, name });
      if (r.data?.token) {
        localStorage.setItem('leadToken', r.data.token);
        if (onLogin) onLogin(r.data.token, null, false);
        else window.location.href = r.data.onboarding ? '/lead/onboarding' : '/lead/dashboard';
      } else if (r.data?.needsEmail) {
        setApplePendingToken(identityToken);
        setFbName(r.data.appleName || '');
        setModalProvider('apple');
        setFbEmailModal(true);
      } else if (r.data?.emailTaken) {
        setFbEmailTaken(true);
        setFbTakenEmail(r.data.email);
        setModalProvider('apple');
        setFbEmailModal(true);
      }
    } catch (err) {
      if (err?.message?.includes('cancel') || err?.message?.includes('dismissed')) {
        // User dismissed — not an error
      } else {
        console.warn('[Apple] fallback to web OAuth:', err.message);
        openOAuth('apple');
      }
    } finally { setLoading(false); }
  };
  const [info, setInfo] = useState('');

  /* ── Native deep-link handler (iOS / Android only)
     Catches mypolicyaccess://oauth?token=JWT&onboarding=true|false
     sent by the server after social auth completes in the system browser. ── */
  useEffect(() => {
    if (!isNative) return;

    let listenerHandle = null;
    CapApp.addListener('appUrlOpen', async ({ url }) => {
      try {
        if (!url.startsWith('mypolicyaccess://oauth')) return;
        // Close the system browser immediately
        Browser.close().catch(() => {});
        const urlObj = new URL(url);
        const token      = urlObj.searchParams.get('token');
        const onboarding = urlObj.searchParams.get('onboarding') === 'true';
        if (!token) return;
        localStorage.setItem('leadToken', token);
        if (onLogin) onLogin(token, null, false);
        else window.location.href = onboarding ? '/lead/onboarding' : '/lead/dashboard';
      } catch (e) {
        console.error('[OAuth deep link] failed:', e);
        setError('Sign-in failed. Please try again.');
      }
    }).then(handle => { listenerHandle = handle; });

    return () => { if (listenerHandle) listenerHandle.remove(); };
  }, [onLogin]);

  /* ── Handle OAuth token redirect + magic link from URL ── */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    // OAuth social login return (?token=JWT)
    const oauthToken = params.get('token');
    if (oauthToken) {
      localStorage.setItem('leadToken', oauthToken);
      window.history.replaceState({}, '', window.location.pathname);
      if (onLogin) onLogin(oauthToken, null, false);
      else window.location.href = '/lead/dashboard';
      return;
    }

    // OAuth error messages
    const oauthErr = params.get('error');
    const oauthErrors = {
      google_not_configured:  'Google sign-in is not yet configured. Please use email/password.',
      google_failed:          'Google sign-in failed. Please try again or use email/password.',
      facebook_not_configured:'Facebook sign-in is not yet configured. Please use email/password.',
      facebook_denied:        'Facebook sign-in was cancelled.',
      facebook_no_email:      'Your Facebook account has no email address. Please use Google Sign-In or email/password instead.',
      facebook_failed:        'Facebook sign-in failed. Please try again or use email/password.',
      apple_not_configured:   'Apple sign-in is not yet configured. Please use email/password.',
      apple_denied:           'Apple sign-in was cancelled.',
      apple_failed:           'Apple sign-in failed. Please try again or use email/password.',
    };
    if (oauthErr && oauthErrors[oauthErr]) {
      setError(oauthErrors[oauthErr]);
      window.history.replaceState({}, '', window.location.pathname);
    }

    // Web/Android Facebook: no email returned → show email collection modal
    const fbNoEmail = params.get('fb_no_email');
    const fbCtx     = params.get('fb_ctx');
    if (fbNoEmail && fbCtx) {
      try {
        const decoded = JSON.parse(atob(fbCtx.replace(/-/g, '+').replace(/_/g, '/')));
        setFbName(decoded.name || '');
      } catch (_) {}
      setFbPendingCtx(fbCtx);
      setFbEmailModal(true);
      window.history.replaceState({}, '', window.location.pathname);
    }

    // Web/Android Facebook: email already taken → show magic link modal directly
    const fbEmailTakenParam = params.get('fb_email_taken');
    const fbEmailParam      = params.get('fb_email');
    if (fbEmailTakenParam && fbEmailParam) {
      setFbEmailTaken(true);
      setFbTakenEmail(decodeURIComponent(fbEmailParam));
      setFbEmailModal(true);
      window.history.replaceState({}, '', window.location.pathname);
    }

    // Magic link
    const magic = params.get('magic');
    if (magic) {
      setLoading(true);
      setInfo('Logging you in…');
      axios.post(`${API}/api/lead-auth/verify-magic-link`, { token: magic })
        .then(r => {
          if (r.data.token) {
            localStorage.setItem('leadToken', r.data.token);
            if (onLogin) onLogin(r.data.token, r.data.account, r.data.mustResetPassword);
            else window.location.href = r.data.mustResetPassword ? '/lead/reset-password' : '/lead/dashboard';
          }
        })
        .catch(e => {
          setError(e.response?.data?.error || 'Login link is invalid or expired.');
          setInfo('');
          // Clean URL
          window.history.replaceState({}, '', window.location.pathname);
        })
        .finally(() => setLoading(false));
    }
  }, []);

  /* ── Send magic link ── */
  const sendMagicLink = async (e) => {
    e.preventDefault();
    setError('');
    if (!email.trim()) { setError('Please enter your email address.'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/api/lead-auth/request-magic-link`, { email: email.trim().toLowerCase() });
      setMagicSent(true);
    } catch(err) {
      setError(err.response?.data?.error || 'Failed to send link. Please try again.');
    } finally { setLoading(false); }
  };

  /* ── Password login ── */
  const loginWithPassword = async (e) => {
    e.preventDefault();
    setError('');
    if (!email.trim() || !password) { setError('Email and password are required.'); return; }
    setLoading(true);
    try {
      const r = await axios.post(`${API}/api/lead-auth/login`, {
        email: email.trim().toLowerCase(),
        password
      });
      if (r.data.token) {
        localStorage.setItem('leadToken', r.data.token);
        if (onLogin) onLogin(r.data.token, r.data.account, r.data.mustResetPassword);
        else window.location.href = r.data.mustResetPassword ? '/lead/reset-password' : '/lead/dashboard';
      }
    } catch(err) {
      setError(err.response?.data?.error || 'Invalid email or password.');
    } finally { setLoading(false); }
  };

  const inp = {
    width: '100%',
    padding: '12px 14px',
    borderRadius: 8,
    border: `1.5px solid ${C.border}`,
    fontSize: 15,
    boxSizing: 'border-box',
    outline: 'none',
    background: '#fff',
    marginBottom: 12,
  };
  const btn = {
    width: '100%',
    padding: '13px',
    background: loading ? '#93c5fd' : C.brand,
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontSize: 15,
    fontWeight: 700,
    cursor: loading ? 'not-allowed' : 'pointer',
    marginTop: 4,
  };

  return (
    <div style={{
      minHeight: '100dvh',
      background: C.bg,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px 16px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    }}>

      {/* ── Facebook Email Modal ────────────────────────────── */}
      {fbEmailModal && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1000,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '20px 16px',
        }}>
          <div style={{
            background: C.card, borderRadius: 16, padding: 28,
            width: '100%', maxWidth: 380,
            boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
          }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
              <div style={{
                width: 38, height: 38, borderRadius: '50%',
                background: modalProvider === 'apple' ? '#000' : '#1877f2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                {modalProvider === 'apple' ? SOCIAL_ICONS.apple : SOCIAL_ICONS.facebook}
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15, color: C.text }}>One more step</div>
                <div style={{ fontSize: 12, color: C.muted }}>{modalProvider === 'apple' ? 'Apple' : 'Facebook'} account verified</div>
              </div>
            </div>

            {fbEmailTaken ? (
              /* ── Email already exists — magic link state ── */
              <>
                <p style={{ fontSize: 13, color: C.muted, lineHeight: 1.6, margin: '0 0 6px' }}>
                  An account already exists for:
                </p>
                <p style={{ fontSize: 14, fontWeight: 700, color: C.text, margin: '0 0 14px', wordBreak: 'break-all' }}>
                  {fbTakenEmail}
                </p>
                <p style={{ fontSize: 13, color: C.muted, lineHeight: 1.6, margin: '0 0 18px' }}>
                  This email was registered with a different sign-in method.
                  {fbMagicSent ? '' : ' Click below to receive a one-click login link to your email and phone.'}
                </p>
                {fbMagicSent ? (
                  <div style={{
                    background: '#f0fdf4', border: '1px solid #bbf7d0',
                    borderRadius: 8, padding: '12px 14px', marginBottom: 14,
                    fontSize: 13, color: '#15803d', lineHeight: 1.6,
                  }}>
                    ✅ Login link sent to <strong>{fbTakenEmail}</strong>.<br />
                    Check your email and SMS — the link expires in 15 minutes.
                  </div>
                ) : (
                  <button
                    onClick={handleFbMagicLink}
                    disabled={fbEmailLoading}
                    style={{
                      width: '100%', padding: '11px 0', background: C.brand,
                      color: '#fff', border: 'none', borderRadius: 8,
                      fontSize: 14, fontWeight: 700, cursor: 'pointer', marginBottom: 10,
                    }}
                  >
                    {fbEmailLoading ? 'Sending…' : 'Send Login Link'}
                  </button>
                )}
                {fbEmailError && <p style={{ color: C.danger, fontSize: 12, marginBottom: 10 }}>{fbEmailError}</p>}
                <button
                  onClick={() => { setFbEmailModal(false); setFbEmailTaken(false); setFbTakenEmail(''); setFbMagicSent(false); setFbEmailError(''); setModalProvider('facebook'); }}
                  style={{
                    width: '100%', padding: '10px 0', background: 'none',
                    color: C.muted, border: `1.5px solid ${C.border}`, borderRadius: 8,
                    fontSize: 13, cursor: 'pointer',
                  }}
                >
                  Close
                </button>
              </>
            ) : (
              /* ── No email — collect email state ── */
              <>
                <p style={{ fontSize: 13, color: C.muted, lineHeight: 1.6, margin: '0 0 18px' }}>
                  Hi{fbName ? ` ${fbName.split(' ')[0]}` : ''}! Your Facebook account doesn't have an email address linked to it.
                  Please enter your email below to continue — we'll use it to set up your account.
                </p>
                <div style={{ marginBottom: 14 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 6 }}>
                    Email Address
                  </label>
                  <input
                    type="email"
                    placeholder="you@example.com"
                    value={fbEmailInput}
                    onChange={e => { setFbEmailInput(e.target.value); setFbEmailError(''); }}
                    onKeyDown={e => e.key === 'Enter' && handleFbEmailSubmit()}
                    autoFocus
                    style={{
                      width: '100%', padding: '10px 12px', fontSize: 14,
                      border: `1.5px solid ${fbEmailError ? C.danger : C.border}`,
                      borderRadius: 8, outline: 'none', boxSizing: 'border-box',
                      color: C.text, background: '#fff',
                    }}
                  />
                  {fbEmailError && (
                    <p style={{ color: C.danger, fontSize: 12, marginTop: 6 }}>{fbEmailError}</p>
                  )}
                </div>
                <button
                  onClick={handleFbEmailSubmit}
                  disabled={fbEmailLoading}
                  style={{
                    width: '100%', padding: '11px 0', background: C.brand,
                    color: '#fff', border: 'none', borderRadius: 8,
                    fontSize: 14, fontWeight: 700, cursor: 'pointer', marginBottom: 10,
                  }}
                >
                  {fbEmailLoading ? 'Signing In…' : 'Continue'}
                </button>
                <button
                  onClick={() => { setFbEmailModal(false); setFbEmailInput(''); setFbEmailError(''); setModalProvider('facebook'); }}
                  style={{
                    width: '100%', padding: '10px 0', background: 'none',
                    color: C.muted, border: `1.5px solid ${C.border}`, borderRadius: 8,
                    fontSize: 13, cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
              </>
            )}
          </div>
        </div>
      )}

      <div style={{ width: '100%', maxWidth: 420 }}>

        {/* Logo / heading */}
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <img src={`${process.env.PUBLIC_URL}/AppLogoLongWithText.png`} alt="My Policy Access" style={{ height: 64, maxWidth: '78%', objectFit: 'contain', marginBottom: 8 }} />
          <p style={{ fontSize: 14, color: C.muted, margin: 0 }}>
            Your Coverage. Your Benefits. Your Agent.
          </p>
        </div>

        <div style={{ background: C.card, borderRadius: 14, border: `1px solid ${C.border}`, padding: '24px 22px' }}>

          {/* Error / info banners */}
          {error && (
            <div style={{ background: '#fef2f2', border: `1px solid #fca5a5`, borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 13, color: C.danger }}>
              {error}
            </div>
          )}
          {info && (
            <div style={{ background: '#eff6ff', border: `1px solid #93c5fd`, borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 13, color: C.brand }}>
              {info}
            </div>
          )}

          {/* ── Social sign-in ── */}
          {!magicSent && (
            <>
              <SocialBtn provider="google"   label="Continue with Google"   onClick={() => openOAuth('google')} />
              <SocialBtn provider="facebook" label="Continue with Facebook" onClick={handleFacebook} />
              {isIos && <SocialBtn provider="apple" label="Continue with Apple" onClick={handleApple} />}
              <Divider text="or sign in with email" />
            </>
          )}

          {/* Magic link sent */}
          {magicSent ? (
            <div style={{ textAlign: 'center', padding: '10px 0' }}>
              <div style={{ fontSize: 46, marginBottom: 10 }}>📨</div>
              <h2 style={{ fontSize: 18, fontWeight: 700, color: C.text, marginBottom: 8 }}>Check your inbox!</h2>
              <p style={{ fontSize: 14, color: C.muted, marginBottom: 20 }}>
                We sent a login link to <strong>{email}</strong>.<br />
                Check your email and SMS — the link expires in 15 minutes.
              </p>
              <button style={{ ...btn, background: 'transparent', color: C.brand, border: `1.5px solid ${C.brand}` }}
                onClick={() => { setMagicSent(false); setError(''); }}>
                ← Try Again
              </button>
            </div>
          ) : (
            <>
              {/* Mode toggle */}
              <div style={{
                display: 'flex',
                background: C.bg,
                borderRadius: 8,
                padding: 3,
                marginBottom: 20,
                gap: 2,
              }}>
                {[
                  { id: 'magic',    label: '✉️ Send Login Link' },
                  { id: 'password', label: '🔑 Password' },
                ].map(t => (
                  <button key={t.id} onClick={() => { setMode(t.id); setError(''); }}
                    style={{
                      flex: 1,
                      padding: '8px 4px',
                      border: 'none',
                      borderRadius: 6,
                      background: mode === t.id ? C.card : 'transparent',
                      boxShadow: mode === t.id ? '0 1px 4px rgba(0,0,0,.1)' : 'none',
                      color: mode === t.id ? C.brand : C.muted,
                      fontWeight: mode === t.id ? 700 : 400,
                      fontSize: 13,
                      cursor: 'pointer',
                    }}>
                    {t.label}
                  </button>
                ))}
              </div>

              {/* Email field (shared) */}
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 5 }}>
                  Email Address
                </label>
                <input
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  style={inp}
                  autoFocus
                  autoComplete="email"
                />
              </div>

              {/* Magic link mode */}
              {mode === 'magic' && (
                <form onSubmit={sendMagicLink}>
                  <p style={{ fontSize: 13, color: C.muted, margin: '0 0 14px', lineHeight: 1.5 }}>
                    We'll send a one-click login link to your email and phone. No password needed.
                  </p>
                  <button type="submit" style={btn} disabled={loading}>
                    {loading ? 'Sending…' : 'Send Login Link'}
                  </button>
                </form>
              )}

              {/* Password mode */}
              {mode === 'password' && (
                <form onSubmit={loginWithPassword}>
                  <div>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 5 }}>
                      Password
                    </label>
                    <input
                      type="password"
                      placeholder="Enter your password"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      style={inp}
                      autoComplete="current-password"
                    />
                  </div>
                  <button type="submit" style={btn} disabled={loading}>
                    {loading ? 'Signing In…' : 'Sign In'}
                  </button>
                  <div style={{ textAlign: 'center', marginTop: 12 }}>
                    <button type="button"
                      onClick={() => { setMode('magic'); setError(''); }}
                      style={{ background: 'none', border: 'none', color: C.brand, fontSize: 13, cursor: 'pointer', padding: 0 }}>
                      Forgot password? Send a login link →
                    </button>
                  </div>
                </form>
              )}
            </>
          )}
        </div>

        {/* Create account link */}
        <p style={{ textAlign: 'center', fontSize: 13, color: C.muted, marginTop: 16 }}>
          New here?{' '}
          <button
            type="button"
            onClick={() => onSignup ? onSignup() : (window.location.href = '/lead/signup')}
            style={{ background: 'none', border: 'none', color: C.brand, fontWeight: 700, cursor: 'pointer', fontSize: 13, padding: 0, textDecoration: 'none' }}
          >
            Create My Account
          </button>
        </p>

        <p style={{ textAlign: 'center', fontSize: 12, color: C.muted, marginTop: 10, lineHeight: 1.6 }}>
          Already have an account from your agent?<br />
          Use email and password above to sign in.
        </p>
      </div>
    </div>
  );
}
